import {
  AGENT_CARD_BUTTON_DARK,
  AGENT_CARD_BUTTON_OUTLINE,
  AgentCard,
  CARD_LINK,
} from '@/components/agent-chat/agent-card.component';
import {
  CardCost,
  CardFooter,
  CardLinkButton,
  CardMeta,
  CardStatus,
  CardText,
} from '@/components/agent-chat/card-parts.component';
import {
  claimProposalShow,
  diagnosisLinkOf,
  onAgentWorkspace,
  panelSearch,
  proposalChangesLine,
  playProposalView,
  quickReplyOptions,
  testRunOf,
} from '@/components/agent-chat/agent-cards.util';
import {
  mergeObjectsLine,
  mergeStartedOf,
  mergeStartedTitle,
  notMergedLine,
  reviewLocation,
} from '@/components/agent-chat/duplicate-cards.util';
import { useMergeProgress } from '@/components/agent-chat/use-merge-progress.hook';
import {
  CONTINUE_CALL_ID,
  PAUSED_HERE,
} from '@/components/agent-chat/paused-turn.util';
import { followingUserText } from '@/components/agent-chat/question-card.util';
import { PlayProposalDetails } from '@/containers/agents/play-proposal-details.component';
import { ReviewCardList } from '@/containers/agents/review-card.component';
import { reviewCardTotalCosts } from '@/containers/agents/review-card.util';
import { turnsOnLine } from '@/containers/agents/agent-enforcement.util';
import { proposalIsBlocked } from '@/containers/agents/play-proposal-review.util';
import { useChatProposalActions } from '@/containers/agents/use-chat-proposal-actions.hook';
import type { WorkspaceLocation } from '@/containers/agents/workspace-location.util';
import { useAgentsAccess } from '@/hooks/use-agents-access.hook';
import { iconLayerGroup, iconSitemap } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useAppDispatch, useAppSelector } from '@/store';
import {
  useCachedAgent,
  useGetPlayProposalQuery,
} from '@/store/features/gtm-agents.feature';
import {
  selectAiChatContext,
  sendAiChatMessage,
} from '@/store/features/ui.feature';
import { useAuiState } from '@assistant-ui/react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  Fragment,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Quick replies (display `quick_replies`)
// ---------------------------------------------------------------------------

/**
 * Clu's quick replies: a wrap row of the card's outline buttons under its
 * answer, a long option cut to the row. A click sends the option as the user's message into the current
 * conversation; once any later user message exists the row is gone.
 */
export function QuickRepliesPart({
  display,
  result,
  toolCallId,
}: {
  display: unknown;
  result: unknown;
  toolCallId?: string;
}) {
  const dispatch = useAppDispatch();
  const followingText = useAuiState((s) =>
    followingUserText(s.thread.messages, s.message.id)
  );
  const sentRef = useRef(false);
  const [sent, setSent] = useState(false);
  const options = quickReplyOptions(display, result);
  // A turn that ended on an error (a call over its hold) carries the same
  // Continue reply but did not pause: the error says why it stopped.
  const failed = useAuiState(
    (s) => s.message.metadata?.custom?.endedInError === true
  );

  if (followingText !== null || options.length === 0) return null;

  return (
    <div className='flex flex-wrap items-center gap-[8px]' data-quick-replies>
      {!failed && toolCallId === CONTINUE_CALL_ID ? (
        <p
          className='w-full text-[14px] leading-[20px] text-foreground'
          data-paused-here
        >
          {PAUSED_HERE}
        </p>
      ) : null}
      {options.map((option) => (
        <button
          key={option}
          type='button'
          disabled={sent}
          onClick={() => {
            if (sentRef.current) return;
            sentRef.current = true;
            setSent(true);
            dispatch(sendAiChatMessage(option));
          }}
          className={cn(AGENT_CARD_BUTTON_OUTLINE, 'max-w-full')}
        >
          <span className='truncate'>{option}</span>
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The play proposal (display `play_proposal`)
// ---------------------------------------------------------------------------

function plural(count: number, one: string, many: string) {
  return `${count} ${count === 1 ? one : many}`;
}

const PROPOSAL_STATUS_LABEL: Record<string, string> = {
  accepted: 'Accepted',
  discarded: 'Discarded',
  superseded: 'Replaced by a newer one',
};

/**
 * A play change Clu proposed: the summary, one line of what changes, its
 * issues (the warnings open Details) and what it turns on, the steps Accept
 * confirms with their most per record or run, and the most it charges a
 * week (or month) in the header. Details and Canvas, which opens the agent's
 * Play tab with the proposal, then Discard and Accept, the canvas's own
 * actions (`useChatProposalActions`), once the live proposal is read as
 * pending. The first time a live proposal shows while that agent's
 * workspace is open, the workspace switches to it by itself, with the chat
 * kept open so the turn goes on. Once the proposal is accepted, discarded or
 * replaced by a newer one the header says so and the footer goes. Part of
 * Agents: nothing shows unless Agents is unlocked (`useAgentsAccess`).
 */
export function PlayProposalCard({ result }: { result: unknown }) {
  const navigate = useNavigate();
  const { unlocked } = useAgentsAccess();
  const contextAgentId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'agent' ? context.agent_id : undefined;
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const view = playProposalView(result);
  const agentId = view?.agentId ?? contextAgentId;
  const proposalId = view?.proposalId;
  const fromHistory =
    !!result &&
    typeof result === 'object' &&
    (result as { __fromHistory?: unknown }).__fromHistory === true;
  const { currentData: proposal } = useGetPlayProposalQuery(
    { agentId: agentId ?? '', proposalId: proposalId ?? '' },
    { skip: !unlocked || !agentId || !proposalId }
  );
  const agent = useCachedAgent(agentId);
  const actions = useChatProposalActions(
    agentId,
    proposal?.id === proposalId ? proposal : undefined
  );
  const [detailsOpen, setDetailsOpen] = useState(false);

  const onWorkspace = onAgentWorkspace(pathname, agentId);
  const open = useCallback(
    (replace: boolean) => {
      if (!agentId || !proposalId) return;
      void navigate({
        to: '/agents/$agentId',
        params: { agentId },
        search: (previous) =>
          panelSearch(previous, onWorkspace, {
            tab: 'play',
            proposal: proposalId,
          }),
        replace,
      });
    },
    [agentId, navigate, onWorkspace, proposalId]
  );

  useEffect(() => {
    if (!unlocked || !proposalId || fromHistory || !onWorkspace) return;
    if (claimProposalShow(proposalId)) open(true);
  }, [unlocked, fromHistory, onWorkspace, open, proposalId]);

  if (!unlocked || !view) return null;
  const current = proposal?.id === proposalId ? proposal : undefined;
  const status = current?.status;
  const resolved = status && status !== 'pending';
  const changes = proposalChangesLine(view, current);
  // Clu turns an ability on only through a proposal the user accepts. While
  // it is open, an ability the agent already has on is not named.
  const turnsOn = turnsOnLine(
    current?.agentChanges,
    resolved ? undefined : agent
  );
  const review = current ?? view;
  const blocked = !resolved && proposalIsBlocked(review);
  const issues = review.issues ?? [];
  const errors = issues.filter((issue) => issue.severity === 'error').length;
  const warnings = issues.filter((issue) => issue.severity !== 'error').length;
  // The period's total, the last of the card's totals (a week's, a month's).
  const totals =
    resolved || blocked ? [] : reviewCardTotalCosts(actions.reviewCard?.totals);
  const total = totals[totals.length - 1];

  const warningsWord = plural(warnings, 'warning', 'warnings');
  const meta = [
    changes ? <span key='changes'>{changes}</span> : null,
    errors ? (
      <span key='errors' className='text-destructive'>
        {plural(errors, 'error', 'errors')}
      </span>
    ) : null,
    !warnings ? null : resolved ? (
      <span key='warnings' className='text-amber-600 dark:text-amber-400'>
        {warningsWord}
      </span>
    ) : (
      <button
        key='warnings'
        type='button'
        onClick={() => setDetailsOpen(true)}
        className='cursor-pointer text-amber-600 underline-offset-[3px] hover:underline dark:text-amber-400'
      >
        {warningsWord}
      </button>
    ),
    turnsOn ? (
      <span key='turns-on' data-proposal-turns-on>
        {turnsOn}
      </span>
    ) : null,
  ].filter(Boolean);

  return (
    <AgentCard
      icon={iconSitemap}
      title='Play change'
      data-play-proposal
      trailing={
        resolved ? (
          <CardStatus tone={status === 'accepted' ? 'success' : undefined}>
            {PROPOSAL_STATUS_LABEL[status] ?? status}
          </CardStatus>
        ) : blocked ? (
          <CardStatus tone='danger'>Blocked</CardStatus>
        ) : total ? (
          <CardCost upTo amount={total.amount} unit={total.unit} />
        ) : undefined
      }
    >
      {/* Answered: the header alone, its status at the right. */}
      {resolved ? undefined : (
        <>
          {view.summary || meta.length ? (
            <div className='flex flex-col gap-[4px]'>
              {view.summary ? <CardText>{view.summary}</CardText> : null}
              {meta.length ? (
                <CardMeta>
                  {meta.map((part, index) => (
                    <Fragment key={index}>
                      {index ? ' · ' : null}
                      {part}
                    </Fragment>
                  ))}
                </CardMeta>
              ) : null}
            </div>
          ) : null}
          {actions.open && actions.reviewCard ? (
            <ReviewCardList card={{ items: actions.reviewCard.items }} />
          ) : null}
          {actions.dialog}
          {
            <CardFooter
              links={
                <>
                  <PlayProposalDetails
                    proposal={review}
                    summary={view.summary}
                    link
                    open={detailsOpen}
                    onOpenChange={setDetailsOpen}
                  />
                  <CardLinkButton
                    onClick={() => open(false)}
                    disabled={!agentId}
                  >
                    Canvas
                  </CardLinkButton>
                </>
              }
              cancel={
                actions.open
                  ? { label: 'Discard', onClick: actions.onDiscard }
                  : undefined
              }
              primary={
                actions.open
                  ? {
                      label: 'Accept',
                      workingLabel: 'Accepting',
                      // False when Accept failed or was cancelled: the
                      // footer unlocks. One that went through stays locked
                      // until the card reads the proposal as Accepted.
                      onClick: actions.onAccept,
                      disabled: !actions.canAccept,
                    }
                  : undefined
              }
              locked={actions.busy}
            />
          }
        </>
      )}
    </AgentCard>
  );
}

// ---------------------------------------------------------------------------
// The connection card (display `connection_required`)
// ---------------------------------------------------------------------------

export { ConnectionRequiredCard } from '@/components/agent-chat/connection-card.component';

// ---------------------------------------------------------------------------
// Links that open a place in the agent's panel, the chat kept open
// ---------------------------------------------------------------------------

/**
 * A text link to a place in an agent's panel: the agent the card names, or
 * the one open in the dock. On that agent's workspace the chat stays open.
 */
function PanelLink({
  agentId,
  location,
  children,
}: {
  agentId: string | undefined;
  location: WorkspaceLocation;
  children: ReactNode;
}) {
  const contextAgentId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'agent' ? context.agent_id : undefined;
  });
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const target = agentId ?? contextAgentId;
  if (!target) return null;
  const onWorkspace = onAgentWorkspace(pathname, target);
  return (
    <Link
      to='/agents/$agentId'
      params={{ agentId: target }}
      search={(previous) => panelSearch(previous, onWorkspace, location)}
      className={CARD_LINK}
    >
      {children}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// A test run (display `agent_test_started`)
// ---------------------------------------------------------------------------

/**
 * "Open run": the run on the agent's Play tab, the chat kept open. Part of
 * Agents: nothing shows unless Agents is unlocked.
 */
export function OpenRunLink({
  runId,
  agentId,
}: {
  runId: string;
  agentId: string | undefined;
}) {
  const { unlocked } = useAgentsAccess();
  if (!unlocked) return null;
  return (
    <PanelLink agentId={agentId} location={{ tab: 'play', run: runId }}>
      Open run
    </PanelLink>
  );
}

// ---------------------------------------------------------------------------
// A failed run, told in the agent's chat (display `run_diagnosis`)
// ---------------------------------------------------------------------------

/**
 * Under the server's line about a failed live run: where a reconnect or a
 * top-up is made, one click in a new tab (the chat stays), and the run on the
 * agent's Play tab. Neither starts a Clu turn, so neither can meet a limit.
 * Part of Agents: nothing shows unless Agents is unlocked.
 */
export function RunDiagnosisActions({
  display,
  result,
}: {
  display: unknown;
  result: unknown;
}) {
  const { unlocked } = useAgentsAccess();
  if (!unlocked) return null;
  const link = diagnosisLinkOf(display, result);
  const run = testRunOf(display, result);
  if (!link && !run) return null;
  return (
    <div data-run-diagnosis className='flex flex-wrap items-center gap-[12px]'>
      {link ? (
        <a
          href={link.url}
          target='_blank'
          rel='noopener noreferrer'
          className={AGENT_CARD_BUTTON_DARK}
        >
          {link.label}
        </a>
      ) : null}
      {run ? <OpenRunLink runId={run.runId} agentId={run.agentId} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// A cleanup play's duplicates (W5 design 10.5)
// ---------------------------------------------------------------------------

/**
 * "Review duplicates": the Runs tab's review of the agent's duplicate
 * groups, one scan's highlighted when a run is named, the chat kept open.
 * Part of Agents: nothing shows unless Agents is unlocked, nor while it loads.
 */
export function ReviewDuplicatesLink({
  agentId,
  runId,
}: {
  agentId: string | undefined;
  runId?: string;
}) {
  const { unlocked } = useAgentsAccess();
  if (!unlocked) return null;
  return (
    <PanelLink agentId={agentId} location={reviewLocation(runId)}>
      Review duplicates
    </PanelLink>
  );
}

/**
 * The merge a confirmed merge-groups card started (display
 * `duplicate_merge_started`), in one line: how it goes and "Review", the
 * Runs tab's review where the groups show as they merge. While it runs, one
 * line under it: the groups by object, how many named groups weren't open
 * any more, and how the merge goes. It follows the merge: "Merged 8 groups
 * in Salesforce" once its run ends, with what didn't merge cleanly (failed,
 * skipped, interrupted, then verified) still said, and a merge that was
 * interrupted reads "Merge interrupted" until a recovery run verifies it
 * (useMergeProgress). Hidden unless Agents is unlocked.
 */
export function DuplicateMergeStartedCard({
  display,
  result,
}: {
  display: unknown;
  result: unknown;
}) {
  const { unlocked } = useAgentsAccess();
  const contextAgentId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'agent' ? context.agent_id : undefined;
  });
  const view = mergeStartedOf(display, result);
  const progress = useMergeProgress(
    view,
    view?.agentId ?? contextAgentId,
    unlocked
  );
  if (!unlocked || !view || !progress) return null;
  const running =
    progress.watching || progress.title === mergeStartedTitle(view);
  // Groups turned away before the run never reach its progress lines, so
  // they stay on the card once it ends too.
  const notMerged = view.notMerged > 0 ? notMergedLine(view.notMerged) : '';
  const lines = (
    running
      ? [mergeObjectsLine(view.byObject), notMerged, ...progress.lines]
      : [notMerged, ...progress.lines]
  ).filter(Boolean);
  return (
    <AgentCard
      icon={iconLayerGroup}
      title={progress.title}
      data-duplicate-merge-started
      trailing={
        <PanelLink agentId={view.agentId} location={reviewLocation()}>
          Review
        </PanelLink>
      }
    >
      {lines.length ? (
        <CardMeta>
          {lines.map((line, index) => (
            <Fragment key={line}>
              {index ? ' · ' : null}
              <span>{line}</span>
            </Fragment>
          ))}
        </CardMeta>
      ) : null}
    </AgentCard>
  );
}
